// FieldVision - ZoneRow.tsx
// Enkelt zone-raekke med farve, input felt og beregninger
// FIX v2: Halveret vertikal spacing, 16px margin paa total
// UPDATE 2025-12-31: Nye zone farver

import React from 'react';

// Zone farver baseret paa NDVI biomasse niveau
export const ZONE_COLORS: Record<number, string> = {
  1: '#F61F1F',  // Roed - lav biomasse
  2: '#F68C1F',  // Orange
  3: '#F6C81F',  // Gul
  4: '#B9F61F',  // Lime
  5: '#26F61F',  // Groen - hoej biomasse
};

// Hover farver (moerkere versioner)
export const ZONE_COLORS_HOVER: Record<number, string> = {
  1: '#B21518',  // Moerk roed
  2: '#CC7319',  // Moerk orange
  3: '#CCA619',  // Moerk gul
  4: '#9ACC19',  // Moerk lime
  5: '#1FCC19',  // Moerk groen
};

export interface ZoneData {
  zone: number;
  rate: number;
  rateInt: number;
  unit: string;
  hectares: number;
  color?: string;
  massKg?: number;
  ndreMin?: number;
  ndreMax?: number;
  description?: string;
}

interface ZoneRowProps {
  zone: ZoneData;
  unit: 'l/ha' | 'kg/ha';
  onRateChange: (zoneNumber: number, newRate: number) => void;
}

const ZoneRow: React.FC<ZoneRowProps> = ({ zone, unit, onRateChange }) => {
  // Ignorer zone 0
  if (zone.zone === 0) return null;

  const color = ZONE_COLORS[zone.zone] || '#9ca3af';
  const total = zone.rate * zone.hectares;
  const unitLabel = unit === 'l/ha' ? 'l' : 'kg';

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value) || 0;
    onRateChange(zone.zone, value);
  };

  return (
    <div className="zone-row">
      <div className="zone-color" style={{ backgroundColor: color }} />
      <span className="zone-label">Zone {zone.zone}</span>
      <div className="zone-input-container">
        <input
          type="number"
          value={zone.rate}
          onChange={handleInputChange}
          min={0}
          step={1}
          className="zone-input"
        />
        <span className="zone-unit">{unit}</span>
      </div>
      <span className="zone-hectares">{zone.hectares.toFixed(1)} ha</span>
      <span className="zone-total">{Math.round(total)} {unitLabel}</span>

      <style>{`
        .zone-row {
          display: grid;
          grid-template-columns: 18px 58px 95px 50px 55px;
          align-items: center;
          gap: 6px;
          padding: 2px 0;
          padding-right: 16px;
        }

        .zone-color {
          width: 14px;
          height: 14px;
          border-radius: 3px;
          flex-shrink: 0;
        }

        .zone-label {
          font-size: 12px;
          font-weight: 500;
          color: #374151;
        }

        .zone-input-container {
          display: flex;
          align-items: center;
          gap: 3px;
        }

        .zone-input {
          width: 55px;
          padding: 4px 6px;
          border: 1px solid #e5e7eb;
          border-radius: 5px;
          font-size: 12px;
          text-align: center;
          color: #111827;
        }

        .zone-input:focus {
          outline: none;
          border-color: #16a34a;
          box-shadow: 0 0 0 2px rgba(22, 163, 74, 0.1);
        }

        .zone-input::-webkit-inner-spin-button,
        .zone-input::-webkit-outer-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }

        .zone-input[type=number] {
          -moz-appearance: textfield;
        }

        .zone-unit {
          font-size: 10px;
          color: #9ca3af;
        }

        .zone-hectares {
          font-size: 11px;
          color: #6b7280;
          text-align: right;
        }

        .zone-total {
          font-size: 12px;
          font-weight: 500;
          color: #16a34a;
          text-align: right;
        }
      `}</style>
    </div>
  );
};

export default ZoneRow;
